import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

interface ImageViewModalProps {
  open: boolean;
  onClose: () => void;
  imageUrls: string[];
  itemName: string;
  onEditImages?: () => void; // Resmi güncelle butonuna basıldığında
}

/**
 * Stok ürününe ait resimleri büyük boyutta görüntüleyen modal
 */
const ImageViewModal: React.FC<ImageViewModalProps> = ({
  open,
  onClose,
  imageUrls,
  itemName,
  onEditImages,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [imageLoading, setImageLoading] = useState(true);

  const isVideoUrl = (url?: string) => {
    if (!url) return false;
    const lower = url.toLowerCase().split('?')[0];
    return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg');
  };

  // Modal açıldığında state'leri sıfırla
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setZoom(1);
      setImageLoading(true);
    }
  }, [open]);

  // Önceki resme git
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageUrls.length - 1));
    setZoom(1);
    setImageLoading(true);
  };

  // Sonraki resme git
  const handleNext = () => {
    setCurrentIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : 0));
    setZoom(1);
    setImageLoading(true);
  };

  // Zoom kontrolü
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  // Klavye navigasyonu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, imageUrls.length]);

  // Resim yüklendi
  const handleImageLoad = () => {
    setImageLoading(false);
  };

  if (!imageUrls || imageUrls.length === 0) {
    return null;
  }

  const currentImageUrl = imageUrls[currentIndex];
  const currentIsVideo = isVideoUrl(currentImageUrl);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          minHeight: isMobile ? '100vh' : '80vh',
        }
      }}
    >
      {/* Başlık */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          py: 1,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {itemName}
          </Typography>
          {imageUrls.length > 1 && (
            <Typography variant="body2" sx={{ color: 'grey.400' }}>
              {currentIndex + 1} / {imageUrls.length}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Zoom Kontrolleri */}
          <IconButton
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            sx={{ color: 'white' }}
            size="small"
          >
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'grey.400', minWidth: '50px', textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            sx={{ color: 'white' }}
            size="small"
          >
            <ZoomInIcon />
          </IconButton>
          
          {/* Kapatma Butonu */}
          <IconButton
            onClick={onClose}
            sx={{ color: 'white', ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Resim Alanı */}
      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 0,
          overflow: 'hidden',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
        }}
      >
        {/* Sol Navigasyon */}
        {imageUrls.length > 1 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 10,
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 40 }} />
          </IconButton>
        )}

        {/* Resim */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            overflow: 'auto',
            p: 2,
          }}
        >
          {/* Loading Spinner */}
          {imageLoading && (
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          )}
          
          {currentIsVideo ? (
            <Box
              component="video"
              src={currentImageUrl}
              controls
              autoPlay
              muted
              loop
              playsInline
              onLoadedData={handleImageLoad}
              sx={{
                maxWidth: '100%',
                maxHeight: isMobile ? 'calc(100vh - 180px)' : 'calc(80vh - 180px)',
                objectFit: 'contain',
                opacity: imageLoading ? 0 : 1,
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                bgcolor: '#000',
              }}
            />
          ) : (
            <Box
              component="img"
              src={currentImageUrl}
              alt={`${itemName} - Resim ${currentIndex + 1}`}
              onLoad={handleImageLoad}
              sx={{
                maxWidth: '100%',
                maxHeight: isMobile ? 'calc(100vh - 180px)' : 'calc(80vh - 180px)',
                objectFit: 'contain',
                transform: `scale(${zoom})`,
                transition: 'transform 0.2s ease',
                cursor: zoom > 1 ? 'grab' : 'default',
                opacity: imageLoading ? 0 : 1,
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              }}
            />
          )}
        </Box>

        {/* Sağ Navigasyon */}
        {imageUrls.length > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 10,
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 40 }} />
          </IconButton>
        )}
      </DialogContent>

      {/* Thumbnail Bar - Birden fazla resim varsa */}
      {imageUrls.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: 3,
            },
          }}
        >
          {imageUrls.map((url, index) => (
            <Box
              key={index}
              component="img"
              src={url}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => {
                setCurrentIndex(index);
                setZoom(1);
                setImageLoading(true);
              }}
              sx={{
                width: 60,
                height: 60,
                objectFit: 'cover',
                borderRadius: 1,
                cursor: 'pointer',
                border: currentIndex === index ? '2px solid' : '2px solid transparent',
                borderColor: currentIndex === index ? 'primary.main' : 'transparent',
                opacity: currentIndex === index ? 1 : 0.6,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  borderColor: 'primary.light',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Alt Butonlar */}
      <DialogActions
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          justifyContent: 'center',
          gap: 2,
          py: 1.5,
        }}
      >
        {onEditImages && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => {
              onClose();
              onEditImages();
            }}
          >
            Resmi Güncelle
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ 
            color: 'white', 
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageViewModal;
